import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import { Avatar as AntdAvatar } from 'antd';
import { Image } from '../../display/image';
import { extractDomProps } from '../../../utils/DomPropsExtractor';
import classnames from 'classnames';
import { AvatarDecorator } from './AvatarDecorators';
import { BadgeProps } from '../../display/badge';

/**
 * {@docCategory Avatar}
 */
export interface AvatarProps {
   /**
    * Link to the image. Can be an inline data link.
    */
   url?: string;

   /**
    * Icon to display.
    */
   icon?: ReactNode;

   /**
    * Number from 1 to 12 for setting the color. This only has an affect if the url is not set.
    * Internally we mod the number by 12 to get an actual color index.
    */
   colorId?: number;

   /**
    * Sets the size of the avatar. It can be default, small, large, or a number
    * @defaultvalue default
    */
   size?: 'default' | 'small' | 'large' | number;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback function when the avatar is clicked.
    */
   onClick?: () => void;

   /**
    * The data-testid
    */
   testId?: string;

   /**
    * The children to display.
    */
   children?: ReactNode;

   /**
    * Top decorator
    */
   topDecorator?: ReactNode | BadgeProps;

   /**
    * Top right decorator
    */
   topRightDecorator?: ReactNode | BadgeProps;

   /**
    * Right decorator
    */
   rightDecorator?: ReactNode | BadgeProps;

   /**
    * Bottom right decorator
    */
   bottomRightDecorator?: ReactNode | BadgeProps;

   /**
    * Bottom decorator
    */
   bottomDecorator?: ReactNode | BadgeProps;

   /**
    * Bottom left decorator
    */
   bottomLeftDecorator?: ReactNode | BadgeProps;

   /**
    * Left decorator
    */
   leftDecorator?: ReactNode | BadgeProps;

   /**
    * Top left decorator
    */
   topLeftDecorator?: ReactNode | BadgeProps;
}

const avatarColorCount = 12;

export const Avatar = (props: AvatarProps) => {
   const containerClassName = classnames('uf-avatar',
                                         props.className,
                                         props.onClick ? 'uf-avatar-cursor-pointer' : undefined);

   const avatarClassName = classnames('uf-avatar-img',
                                      getColorIdClassName(props));

   return wrapInDecorator(props, <span
      {...extractDomProps(props)}
      className={containerClassName}
      onClick={props.onClick}
      data-testid={props.testId}
   >
      <AntdAvatar
         src={getUrl(props)}
         icon={getIcon(props)}
         size={props.size}
         className={avatarClassName}
         style={props.style}
         shape={'circle'}
      >
         {props.children}
      </AntdAvatar>
   </span>);
};

const wrapInDecorator = (props: AvatarProps, component: ReactElement) => {
   if (hasDecorators(props)) {
      return (
         <AvatarDecorator
            size={getSize(props)}
            top={props.topDecorator}
            topRight={props.topRightDecorator}
            right={props.rightDecorator}
            bottomRight={props.bottomRightDecorator}
            bottom={props.bottomDecorator}
            bottomLeft={props.bottomLeftDecorator}
            left={props.leftDecorator}
            topLeft={props.topLeftDecorator}
         >
            {component}
         </AvatarDecorator>
      );
   }

   return component;
};

const hasDecorators = (props: AvatarProps) => {
   return props.topDecorator || props.topRightDecorator || props.rightDecorator || props.bottomRightDecorator || props.bottomDecorator || props.bottomLeftDecorator || props.leftDecorator || props.topLeftDecorator;
};

const getSize = (props: AvatarProps) => {
   if (props.size === undefined || props.size === 'default') {
      return 32;
   }

   if (props.size === 'small') {
      return 24;
   }

   if (props.size === 'large') {
      return 40;
   }

   return props.size;
};

const getColorIdClassName = (props: AvatarProps) => {
   if (!props.url && (props.icon || props.children)) {
      const colorId = props.colorId || 1;
      const avatarColorIndex = ((colorId - 1) % avatarColorCount) + 1;
      return ' uf-avatar-img-color-' + avatarColorIndex;
   }

   return undefined;
};

const getUrl = (props: AvatarProps) => {
   if (props.url) {
      return props.url;
   }

   if (!props.icon && !props.children) {
      return `data:image/svg+xml;base64,${defaultAvatarSvg}`;
   }

   return undefined;
};

const getIcon = (props: AvatarProps) => {
   if (props.icon) {
      return props.icon;
   }

   if (props.url) {
      return <Image src={`data:image/svg+xml;base64,${defaultAvatarSvg}`}/>;
   }

   return undefined;
};

const defaultAvatarSvg = btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 106 106"><defs><style>.cls-1{fill:#c5c5c5;}.cls-2{fill:#717171;}</style></defs><g id="Default_Avatar" data-name="Default Avatar"><circle class="cls-1" cx="53" cy="53" r="53"/><path class="cls-2" d="M72.55,80c8.57,0,15.52-3.22,15.52-7.2H57C57,76.79,64,80,72.55,80Z"/><rect class="cls-2" x="45.77" y="53.02" width="10" height="14.5" rx="5"/><rect class="cls-2" x="87.24" y="53.02" width="10" height="14.5" rx="5"/></g></svg>`);
